import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Music2, Type, Gauge, Sliders, X, Plus, Smile } from "lucide-react";
import { FILTER_PRESETS, FILTER_KEYS, filterCss, SPEED_STEPS, STICKER_GROUPS } from "@/lib/mediaEffects";
import MusicPicker from "@/components/feed/MusicPicker";

const TEXT_COLORS = ["#FFFFFF", "#0A0A0A", "#FACC15", "#EF4444", "#22C55E", "#3B82F6"];

/** Media-Effekt-Editor (Phase 3) · Live-Vorschau mit Filter, Tempo (Video),
 *  Textoverlays (ziehbar) und Musikauswahl. Speichert alles als Metadaten. */
export default function MediaEffectsEditor({ mediaUrl, mediaType, effects, music, onEffectsChange, onMusicChange, allowMusic = true }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState("filter");
  const [showMusic, setShowMusic] = useState(false);
  const [activeText, setActiveText] = useState(null);
  const stageRef = useRef(null);
  const videoRef = useRef(null);
  const dragRef = useRef(null);

  const e = effects || { filter: "none", speed: 1, texts: [], stickers: [] };
  const setFilter = (f) => onEffectsChange({ ...e, filter: f });
  const setSpeed = (s) => {
    onEffectsChange({ ...e, speed: s });
    if (videoRef.current) videoRef.current.playbackRate = s;
  };
  const addText = () => {
    const nt = { id: Date.now(), text: t("effects.sample_text", { defaultValue: "Text" }), x: 0.5, y: 0.5, color: "#FFFFFF", size: 28 };
    onEffectsChange({ ...e, texts: [...(e.texts || []), nt] });
    setActiveText(nt.id);
    setTab("text");
  };
  const updateText = (id, patch) => onEffectsChange({ ...e, texts: (e.texts || []).map((x) => x.id === id ? { ...x, ...patch } : x) });
  const removeText = (id) => { onEffectsChange({ ...e, texts: (e.texts || []).filter((x) => x.id !== id) }); setActiveText(null); };

  // Sticker (lizenzfreie Emoji-Overlays, frei platzierbar)
  const addSticker = (emoji) => {
    const n = (e.stickers || []).length;
    const ns = { id: Date.now() + Math.random(), emoji,
      x: Math.min(0.8, Math.max(0.2, 0.5 + ((n % 4) - 1.5) * 0.08)),
      y: Math.min(0.8, Math.max(0.2, 0.5 + (Math.floor(n / 4) - 1) * 0.08)), size: 56 };
    onEffectsChange({ ...e, stickers: [...(e.stickers || []), ns] });
    setActiveText(ns.id);
  };
  const updateSticker = (id, patch) => onEffectsChange({ ...e, stickers: (e.stickers || []).map((x) => x.id === id ? { ...x, ...patch } : x) });
  const removeSticker = (id) => { onEffectsChange({ ...e, stickers: (e.stickers || []).filter((x) => x.id !== id) }); setActiveText(null); };

  const onPointerDown = (kind, id) => (ev) => { ev.stopPropagation(); dragRef.current = { kind, id }; setActiveText(id); };
  const onPointerMove = (ev) => {
    if (!dragRef.current || !stageRef.current) return;
    const r = stageRef.current.getBoundingClientRect();
    const cx = (ev.touches ? ev.touches[0].clientX : ev.clientX) - r.left;
    const cy = (ev.touches ? ev.touches[0].clientY : ev.clientY) - r.top;
    const x = Math.min(0.97, Math.max(0.03, cx / r.width));
    const y = Math.min(0.97, Math.max(0.03, cy / r.height));
    if (dragRef.current.kind === "sticker") updateSticker(dragRef.current.id, { x, y });
    else updateText(dragRef.current.id, { x, y });
  };
  const onPointerUp = () => { dragRef.current = null; };

  const Stage = (
    <div className="flex justify-center">
    <div
      ref={stageRef}
      data-testid="effects-stage"
      className="relative inline-block max-h-72 rounded-2xl border border-[#E5E5E5] overflow-hidden bg-black select-none"
      onMouseMove={onPointerMove} onMouseUp={onPointerUp} onMouseLeave={onPointerUp}
      onTouchMove={onPointerMove} onTouchEnd={onPointerUp}
    >
      {mediaType === "VIDEO" ? (
        <video ref={videoRef} src={mediaUrl} controls playsInline
          className="block max-h-72 max-w-full w-auto object-contain" style={{ filter: filterCss(e.filter) }}
          onLoadedMetadata={() => { if (videoRef.current) videoRef.current.playbackRate = e.speed || 1; }} />
      ) : (
        <img src={mediaUrl} alt="" className="block max-h-72 max-w-full w-auto object-contain" style={{ filter: filterCss(e.filter) }} />
      )}
      {(e.texts || []).map((tx) => (
        <div
          key={tx.id}
          data-testid={`effects-text-${tx.id}`}
          onMouseDown={onPointerDown("text", tx.id)}
          onTouchStart={onPointerDown("text", tx.id)}
          className={`absolute cursor-move font-bold leading-tight text-center px-1 ${activeText === tx.id ? "ring-1 ring-white/70 rounded" : ""}`}
          style={{
            left: `${tx.x * 100}%`, top: `${tx.y * 100}%`, transform: "translate(-50%,-50%)",
            color: tx.color, fontSize: `${tx.size}px`, textShadow: "0 1px 3px rgba(0,0,0,0.6)", maxWidth: "90%", touchAction: "none",
          }}
        >
          {tx.text}
        </div>
      ))}
      {(e.stickers || []).map((st) => (
        <div
          key={st.id}
          data-testid={`effects-sticker-${st.id}`}
          onMouseDown={onPointerDown("sticker", st.id)}
          onTouchStart={onPointerDown("sticker", st.id)}
          className={`absolute cursor-move leading-none select-none ${activeText === st.id ? "ring-1 ring-white/70 rounded" : ""}`}
          style={{
            left: `${st.x * 100}%`, top: `${st.y * 100}%`, transform: "translate(-50%,-50%)",
            fontSize: `${st.size}px`, filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.4))", touchAction: "none",
          }}
        >
          {st.emoji}
        </div>
      ))}
    </div>
    </div>
  );

  return (
    <div data-testid="media-effects-editor" className="space-y-2">
      {Stage}

      <div className="flex gap-2">
        {[["filter", Sliders, t("effects.filter", { defaultValue: "Filter" })],
          ...(mediaType === "VIDEO" ? [["speed", Gauge, t("effects.speed", { defaultValue: "Tempo" })]] : []),
          ["text", Type, t("effects.text", { defaultValue: "Text" })],
          ["sticker", Smile, t("effects.stickers", { defaultValue: "Sticker" })],
          ...(allowMusic ? [["music", Music2, t("effects.music", { defaultValue: "Musik" })]] : [])].map(([key, Icon, label]) => (
          <button key={key} data-testid={`effects-tab-${key}`}
            onClick={() => { if (key === "music") setShowMusic(true); else setTab(key); }}
            className={`flex-1 flex flex-col items-center gap-0.5 py-1.5 rounded-xl text-[10px] font-medium transition-colors ${tab === key && key !== "music" ? "bg-[#0A0A0A] text-white" : "bg-[#F5F5F5] text-[#666]"}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {tab === "filter" && (
        <div className="flex gap-2 overflow-x-auto scrollbar-hide py-1">
          {FILTER_KEYS.map((k) => (
            <button key={k} data-testid={`effects-filter-${k}`} onClick={() => setFilter(k)}
              className={`shrink-0 flex flex-col items-center gap-1 ${e.filter === k ? "opacity-100" : "opacity-70"}`}>
              <div className="w-12 h-12 rounded-lg overflow-hidden border-2" style={{ borderColor: e.filter === k ? "#0A0A0A" : "transparent" }}>
                {mediaType === "VIDEO"
                  ? <div className="w-full h-full bg-[#333]" style={{ filter: filterCss(k) }} />
                  : <img src={mediaUrl} alt="" className="w-full h-full object-cover" style={{ filter: filterCss(k) }} />}
              </div>
              <span className="text-[9px] text-[#666]">{t(`effects.filter_${k}`, { defaultValue: FILTER_PRESETS[k].label })}</span>
            </button>
          ))}
        </div>
      )}

      {tab === "speed" && mediaType === "VIDEO" && (
        <div className="flex gap-2 py-1">
          {SPEED_STEPS.map((s) => (
            <button key={s} data-testid={`effects-speed-${s}`} onClick={() => setSpeed(s)}
              className={`flex-1 py-1.5 rounded-full text-xs font-medium ${e.speed === s ? "bg-[#0A0A0A] text-white" : "bg-[#F5F5F5] text-[#666]"}`}>
              {s}×
            </button>
          ))}
        </div>
      )}

      {tab === "text" && (
        <div className="space-y-2 py-1">
          <button data-testid="effects-add-text" onClick={addText}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#E5E5E5] text-xs text-[#666] hover:bg-[#F5F5F5]">
            <Plus size={13} /> {t("effects.add_text", { defaultValue: "Text hinzufügen" })}
          </button>
          {(e.texts || []).map((tx) => (
            <div key={tx.id} className="flex items-center gap-2 bg-[#F9F9F9] rounded-xl p-2">
              <input data-testid={`effects-text-input-${tx.id}`} value={tx.text} onChange={(ev) => updateText(tx.id, { text: ev.target.value.slice(0, 80) })}
                onFocus={() => setActiveText(tx.id)}
                className="flex-1 bg-white border border-[#E5E5E5] rounded-lg px-2 py-1 text-xs" />
              <div className="flex gap-1">
                {TEXT_COLORS.map((c) => (
                  <button key={c} onClick={() => updateText(tx.id, { color: c })}
                    className={`w-5 h-5 rounded-full border ${tx.color === c ? "ring-2 ring-offset-1 ring-[#0A0A0A]" : "border-[#DDD]"}`}
                    style={{ background: c }} />
                ))}
              </div>
              <button data-testid={`effects-text-remove-${tx.id}`} onClick={() => removeText(tx.id)} className="text-red-400"><X size={14} /></button>
            </div>
          ))}
          <p className="text-[10px] text-[#999]">{t("effects.drag_hint", { defaultValue: "Text im Bild ziehen, um ihn zu platzieren." })}</p>
        </div>
      )}

      {tab === "sticker" && (
        <div data-testid="effects-sticker-panel" className="space-y-2 py-1">
          {(e.stickers || []).length > 0 && (() => {
            const active = (e.stickers || []).find((s) => s.id === activeText) || (e.stickers || [])[(e.stickers || []).length - 1];
            return (
              <div className="flex items-center gap-2 bg-[#F9F9F9] rounded-xl p-2">
                <span className="text-xl leading-none">{active.emoji}</span>
                <input data-testid="effects-sticker-size" type="range" min={24} max={140} value={active.size}
                  onChange={(ev) => updateSticker(active.id, { size: Number(ev.target.value) })}
                  className="flex-1 accent-[#0A0A0A]" />
                <button data-testid={`effects-sticker-remove-${active.id}`} onClick={() => removeSticker(active.id)} className="text-red-400"><X size={14} /></button>
              </div>
            );
          })()}
          <div className="max-h-40 overflow-y-auto space-y-2">
            {STICKER_GROUPS.map((g) => (
              <div key={g.key}>
                <p className="text-[9px] uppercase tracking-wider text-[#AAA] mb-1">{t(`effects.sticker_${g.key}`, { defaultValue: g.key })}</p>
                <div className="flex flex-wrap gap-1">
                  {g.emojis.map((emoji) => (
                    <button key={emoji} data-testid={`effects-sticker-add-${emoji}`} onClick={() => addSticker(emoji)}
                      className="w-9 h-9 flex items-center justify-center text-xl rounded-lg hover:bg-[#F0F0F0] active:scale-90 transition-transform">
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-[#999]">{t("effects.sticker_hint", { defaultValue: "Sticker antippen zum Hinzufügen, im Bild ziehen zum Platzieren." })}</p>
        </div>
      )}

      {allowMusic && music && (
        <div data-testid="effects-music-chip" className="flex items-center gap-2 bg-[#F0F7FF] border border-[#DBEAFE] rounded-xl px-3 py-2">
          <Music2 size={14} className="text-[#3B82F6]" />
          <span className="flex-1 text-xs text-[#0A0A0A] truncate">{music.title}</span>
          <button data-testid="effects-music-remove" onClick={() => onMusicChange(null)} className="text-red-400"><X size={14} /></button>
        </div>
      )}

      {allowMusic && showMusic && (
        <MusicPicker selectedId={music?.id} onClose={() => setShowMusic(false)}
          onSelect={(track) => onMusicChange({ id: track.id, title: track.title, artist: track.artist, preview_url: track.preview_url, source: track.source })} />
      )}
    </div>
  );
}
